import React, { useState, useEffect, useRef, useCallback } from "react";
import "./ProductSlider.css";
import "./Detail.css";

import { useSelector } from "react-redux";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import "react-lazy-load-image-component/src/effects/blur.css";
import { LazyLoadImage } from "react-lazy-load-image-component";

function ProductSlider({ selectProduct }) {
  const selectedVariant = useSelector(
    (state) => state.productDetail?.selectedVariant
  );

  const [sliderState, setSliderState] = useState({
    slideIndex: 1,
    images: [],
    width: 0,
    start: 0,
    change: 9,
    hovering: false,
    bigImage: "",
  });

  const [open, setOpen] = useState(false);
  const handleCloses = () => {
    setOpen(false);
  };
  const handleOpen = () => {
    setOpen(true);
  };

  const slideRef = useRef();

  useEffect(() => {
    handleOpen();
    if (selectProduct && selectProduct?.length > 0) {
      const selectedImages = selectProduct[0]?.SKU_Images?.find(
        (product) => product?.SKU_ID === selectedVariant?.SKU_ID
      );
      let imagesArray = [];
      if (selectedImages) {
        imagesArray = selectedImages?.images.map(
          (imageUrl) =>
            `https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_PRODUCTS_IMAGE/${imageUrl}`
        );
        handleCloses();
      } else if (selectProduct[0]?.images) {
        imagesArray = selectProduct[0]?.images[0]?.map(
          (imageUrl) =>
            `https://cyclecircle.blr1.cdn.digitaloceanspaces.com/${process.env.REACT_APP_IMAGE_BASE}_PRODUCTS_IMAGE/${imageUrl}`
        );
        handleCloses();
      }
      setSliderState((prevState) => ({
        ...prevState,
        images: imagesArray,
        bigImage: imagesArray && imagesArray.length > 0 && imagesArray[0],
        slideIndex: 1,
      }));
    }
  }, [selectProduct, selectedVariant]);

  useEffect(() => {
    if (!slideRef.current) return;

    const scrollWidth = slideRef.current.scrollWidth;
    const childrenElementCount = slideRef.current.childElementCount;
    const width = scrollWidth / childrenElementCount;

    setSliderState((prevState) => ({
      ...prevState,
      width: width,
    }));
  }, []);

  const plusSlides = useCallback((n) => {
    if (sliderState.images.length === 0) {
      // Handle the case when images are not yet loaded
      return;
    }

    const newIndex =
      ((sliderState.slideIndex - 1 + n + sliderState.images.length) %
        sliderState.images.length) +
      1;
    setSliderState((prevState) => ({
      ...prevState,
      slideIndex: newIndex,
      bigImage: sliderState.images[newIndex - 1],
    }));
  });

  useEffect(() => {
    const interval = setInterval(() => {
      if (!sliderState.hovering) {
        plusSlides(1);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [sliderState.hovering, sliderState.slideIndex, plusSlides]);

  function handleDragStart(e) {
    setSliderState((prevState) => ({
      ...prevState,
      start: e.clientX,
    }));
  }

  function handleDragOver(e) {
    const touch = e.clientX;
    setSliderState((prevState) => ({
      ...prevState,
      change: prevState.start - touch,
    }));
  }

  function handleDragEnd() {
    if (sliderState.change > 0) {
      slideRef.current.scrollLeft += sliderState.width;
    } else {
      slideRef.current.scrollLeft -= sliderState.width;
    }
  }

  useEffect(() => {
    if (!slideRef.current || !sliderState.width) return;

    const numOfThumb = Math.round(
      slideRef.current.offsetWidth / sliderState.width
    );
    slideRef.current.scrollLeft =
      sliderState.slideIndex > numOfThumb
        ? (sliderState.slideIndex - 1) * sliderState.width
        : 0;
  }, [sliderState.width, sliderState.slideIndex]);

  const lensRef = useRef(null);
  const productImgRef = useRef(null);
  const largeImageContainerRef = useRef(null);

  useEffect(() => {
    const moveLens = (e) => {
      const lens = lensRef.current;
      const productImg = productImgRef.current;
      const largeImageContainer = largeImageContainerRef.current;

      const productImgRect = productImg.getBoundingClientRect();

      // Adjust for page scroll
      let x =
        e.pageX - productImgRect.left - window.scrollX - lens.offsetWidth / 2;
      let y =
        e.pageY - productImgRect.top - window.scrollY - lens.offsetHeight / 2;

      // Restrict lens movement within the product image
      const maxX = productImgRect.width - lens.offsetWidth;
      const maxY = productImgRect.height - lens.offsetHeight;

      if (x > maxX) x = maxX;
      if (x < 0) x = 0;

      if (y > maxY) y = maxY;
      if (y < 0) y = 0;

      // Position the lens
      lens.style.top = `${y}px`;
      lens.style.left = `${x}px`;

      // Set zoom scaling
      const cx = largeImageContainer.offsetWidth / lens.offsetWidth;
      const cy = largeImageContainer.offsetHeight / lens.offsetHeight;

      // Update background position and size for large image container
      largeImageContainer.style.background = `url('${productImg.src}') 
        -${x * cx}px -${y * cy}px 
        / ${productImgRect.width * cx}px ${productImgRect.height * cy}px 
        no-repeat`;

      lens.classList.add("active");
      largeImageContainer.classList.add("active");
    };

    const leaveLens = () => {
      if (lensRef.current) lensRef.current.classList.remove("active");
      if (largeImageContainerRef.current) {
        largeImageContainerRef.current.classList.remove("active");
        largeImageContainerRef.current.style.background = "none";
      }
    };

    const lens = lensRef.current;
    const productImg = productImgRef.current;

    if (lens && productImg) {
      productImg.addEventListener("mousemove", moveLens);
      lens.addEventListener("mousemove", moveLens);
      lens.addEventListener("mouseleave", leaveLens);
      productImg.addEventListener("mouseleave", leaveLens);

      // Cleanup event listeners on component unmount
      return () => {
        productImg.removeEventListener("mousemove", moveLens);
        lens.removeEventListener("mousemove", moveLens);
        lens.removeEventListener("mouseleave", leaveLens);
        productImg.removeEventListener("mouseleave", leaveLens);
      };
    }
  }, []);

  return (
    <div className="product-page-img">
      <div className="big-images">
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={open} // Show backdrop when either open or filtering state is true
          onClick={handleCloses}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <div className="mySlider-div">
          <div
            className="mySlides"
            onMouseEnter={() =>
              setSliderState((prevState) => ({ ...prevState, hovering: true }))
            }
            onMouseLeave={() =>
              setSliderState((prevState) => ({ ...prevState, hovering: false }))
            }
          >
            <img
              ref={productImgRef}
              src={sliderState.bigImage}
              alt=""
              className="w-full h-full object-contain"
            />
            <div className="magnifier-lens" ref={lensRef}></div>
            <div
              className="largeImageContainer1"
              ref={largeImageContainerRef}
            ></div>
          </div>
          <div
            className="mobile-mySlides"
            onMouseEnter={() =>
              setSliderState((prevState) => ({ ...prevState, hovering: true }))
            }
            onMouseLeave={() =>
              setSliderState((prevState) => ({ ...prevState, hovering: false }))
            }
          >
            <LazyLoadImage
              className="mobile-big-image"
              src={sliderState.bigImage}
              alt=""
              effect="blur"
              wrapperProps={{
                // If you need to, you can tweak the effect transition using the wrapper style.
                style: { transitionDelay: "1s" },
              }}
            />
          </div>
        </div>
        <span className="prev" onClick={() => plusSlides(-1)}>
          &#10094;
        </span>
        <span className="next" onClick={() => plusSlides(1)}>
          &#10095;
        </span>
      </div>

      <div
        className="slider-img"
        draggable={true}
        ref={slideRef}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
      >
        {sliderState.images.map((image, index) => (
          <div
            key={index}
            className={`slider-box ${
              index + 1 === sliderState.slideIndex ? "active" : ""
            }`}
            onClick={() => {
              setSliderState((prevState) => ({
                ...prevState,
                slideIndex: index + 1,
                bigImage: image,
              }));
            }}
          >
            <Backdrop
              sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
              open={open} // Show backdrop when either open or filtering state is true
              onClick={handleCloses}
            >
              <CircularProgress color="inherit" />
            </Backdrop>
            <LazyLoadImage
              src={image}
              alt=""
              effect="blur"
              wrapperProps={{
                // If you need to, you can tweak the effect transition using the wrapper style.
                style: { transitionDelay: "1s" },
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default ProductSlider;
